import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Container, Paper, TextField, Button, Typography,
    Box, Grid, Alert, Autocomplete, Divider, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Select, MenuItem, FormControl, InputLabel, InputAdornment
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { History as HistoryIcon, Gavel as GavelIcon } from '@mui/icons-material';
import api from '../services/api';

function ProcessoForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const theme = useTheme();
    const [error, setError] = useState('');
    const [clientes, setClientes] = useState([]);
    const [advogados, setAdvogados] = useState([]);
    const [statusList, setStatusList] = useState([]);
    const [tipoBeneficioList, setTipoBeneficioList] = useState([]);
    const [tipoAcaoList, setTipoAcaoList] = useState([]);
    const [localizacaoList, setLocalizacaoList] = useState([]);
    const [varaList, setVaraList] = useState([]);
    const [andamentos, setAndamentos] = useState([]);
    const [parteExternaList, setParteExternaList] = useState([]);
    const [parteExternaProcesso, setParteExternaProcesso] = useState([]);
    const [gerandoNumero, setGerandoNumero] = useState(false);
    const [formData, setFormData] = useState({
        numero_processo: '',
        data_distribuicao: '',
        status: '',
        tipo_beneficio: '',
        tipo_acao: '',
        valor_causa: '',
        cliente_id: '',
        advogado_id: '',
        tempo_contribuicao: '',
        vara: '',
        comarca: '',
        pasta: '',
        data: '',
        data_ajuizamento: '',
        localizacao: '',
        data_processo: '',
        data_implantacao: '',
        historico_geral: '',
        situacao: '',
        materia: ''
    });

    // Função auxiliar para encontrar ou criar valor para Autocomplete
    const getValueForAutocomplete = (list, value, fieldName = 'valor') => {
        if (!value) return null;
        // Primeiro tenta encontrar na lista
        const found = list.find(item => item[fieldName] === value);
        if (found) return found;
        // Se não encontrou, cria um objeto temporário com o valor do banco
        return { [fieldName]: value, id: null, isCustom: true };
    };

    // Função para formatar data do banco (YYYY-MM-DD ou YYYY-MM-DD HH:mm:ss) para input date (YYYY-MM-DD)
    const formatarDataParaInput = (data) => {
        if (!data) return '';
        
        // Se está no formato YYYY-MM-DD ou YYYY-MM-DD HH:mm:ss, extrai apenas a parte da data
        if (typeof data === 'string') {
            // Remove hora se houver e pega apenas YYYY-MM-DD
            const partes = data.split('T')[0].split(' ')[0].split('-');
            if (partes.length === 3) {
                // Retorna YYYY-MM-DD (formato esperado pelo input type="date")
                return `${partes[0]}-${partes[1]}-${partes[2]}`;
            }
        }
        
        // Se já está em formato YYYY-MM-DD, retorna como está
        if (typeof data === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(data)) {
            return data;
        }
        
        return '';
    };

    const loadClientes = async () => {
        try {
            const response = await api.get('/clientes');
            setClientes(response.data);
        } catch (error) {
            console.error('Erro ao carregar clientes:', error);
        }
    };

    const loadAdvogados = async () => {
        try {
            const response = await api.get('/advogados');
            setAdvogados(response.data);
        } catch (error) {
            console.error('Erro ao carregar advogados:', error);
        }
    };

    const loadStatusList = async () => {
        try {
            const response = await api.get('/status-lista?ativo=true');
            setStatusList(response.data);
        } catch (error) {
            console.error('Erro ao carregar lista de status:', error);
        }
    };

    const loadTipoBeneficioList = async () => {
        try {
            const response = await api.get('/tipo-beneficio-lista?ativo=true');
            setTipoBeneficioList(response.data);
        } catch (error) {
            console.error('Erro ao carregar lista de tipos de benefício:', error);
        }
    };

    const loadTipoAcaoList = async () => {
        try {
            const response = await api.get('/tipo-acao-lista?ativo=true');
            setTipoAcaoList(response.data);
        } catch (error) {
            console.error('Erro ao carregar lista de tipos de ação:', error);
        }
    };

    const loadLocalizacaoList = async () => {
        try {
            const response = await api.get('/localizacao-lista?ativo=true');
            setLocalizacaoList(response.data);
        } catch (error) {
            console.error('Erro ao carregar lista de localização:', error);
        }
    };

    const loadVaraList = async () => {
        try {
            const response = await api.get('/vara-lista?ativo=true');
            setVaraList(response.data);
        } catch (error) {
            console.error('Erro ao carregar lista de varas:', error);
        }
    };

    const loadParteExternaList = async () => {
        try {
            const response = await api.get('/parte-externa?ativo=true');
            setParteExternaList(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar lista de partes externas:', error);
        }
    };

    const loadProcesso = useCallback(async () => {
        if (!id) return;
        try {
            const response = await api.get(`/processos/${id}`);
            const dados = response.data;
            
            // Formata datas para o input (YYYY-MM-DD, sem timezone)
            if (dados.data_distribuicao) {
                dados.data_distribuicao = formatarDataParaInput(dados.data_distribuicao);
            }
            if (dados.data_ajuizamento) {
                dados.data_ajuizamento = formatarDataParaInput(dados.data_ajuizamento);
            }
            if (dados.data_processo) {
                dados.data_processo = formatarDataParaInput(dados.data_processo);
            }
            if (dados.data_implantacao) {
                dados.data_implantacao = formatarDataParaInput(dados.data_implantacao);
            }
            
            setFormData({
                ...dados,
                cliente_id: dados.cliente_id || '',
                advogado_id: dados.advogado_id || ''
            });
        } catch (error) {
            console.error('Erro ao carregar processo:', error);
            setError('Erro ao carregar dados do processo');
        }
    }, [id]);

    const loadAndamentos = useCallback(async () => {
        if (!id) return;
        try {
            const response = await api.get(`/andamentos?processo_id=${id}`);
            setAndamentos(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar andamentos:', error);
        }
    }, [id]);

    const loadParteExternaProcesso = useCallback(async () => {
        if (!id) return;
        try {
            const response = await api.get(`/processo-parte-externa/processo/${id}`);
            setParteExternaProcesso(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar partes externas do processo:', error);
        }
    }, [id]);

    useEffect(() => {
        loadClientes();
        loadAdvogados();
        loadStatusList();
        loadTipoBeneficioList();
        loadTipoAcaoList();
        loadLocalizacaoList();
        loadVaraList();
        loadParteExternaList();
        if (id) {
            loadProcesso();
            loadAndamentos();
            loadParteExternaProcesso();
        }
    }, [id, loadProcesso, loadAndamentos, loadParteExternaProcesso]);

    // Função para formatar data para exibição (YYYY-MM-DD -> DD/MM/YYYY) sem conversão de timezone
    const formatarDataExibicao = (data) => {
        if (!data) return '-';
        try {
            // IMPORTANTE: Sempre extrai diretamente da string para evitar problemas de timezone
            // Remove timezone e hora, mantém apenas YYYY-MM-DD
            if (typeof data === 'string') {
                const dataLimpa = data.split('T')[0].split(' ')[0];
                const match = dataLimpa.match(/(\d{4})-(\d{2})-(\d{2})/);
                if (match) {
                    // Retorna DD/MM/YYYY
                    return `${match[3]}/${match[2]}/${match[1]}`;
                }
            }
            // Fallback: tenta usar Date, mas com cuidado para timezone
            const dateObj = new Date(data);
            if (isNaN(dateObj.getTime())) return data;
            // Usa métodos UTC para evitar conversão de timezone
            const dia = String(dateObj.getUTCDate()).padStart(2, '0');
            const mes = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
            const ano = dateObj.getUTCFullYear();
            return `${dia}/${mes}/${ano}`;
        } catch {
            return data;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.numero_processo || !formData.cliente_id) {
            setError('Número do processo e cliente são obrigatórios');
            return;
        }

        try {
            let processoId;
            if (id) {
                await api.put(`/processos/${id}`, formData);
                processoId = id;
            } else {
                const response = await api.post('/processos', formData);
                processoId = response.data.id;
            }

            // Salvar partes externas
            if (processoId) {
                // Remove todas as partes externas atuais
                await api.delete(`/processo-parte-externa/processo/${processoId}`);
                
                // Adiciona as novas partes externas selecionadas
                for (const parte of parteExternaProcesso) {
                    try {
                        await api.post('/processo-parte-externa', {
                            processo_id: processoId,
                            parte_externa_id: parte.parte_externa_id || parte.id
                        });
                    } catch (err) {
                        console.error('Erro ao adicionar parte externa:', err);
                    }
                }
            }

            navigate('/processos');
        } catch (error) {
            console.error('Erro ao salvar processo:', error);
            setError(error.response?.data?.error || 'Erro ao salvar processo');
        }
    };

    const handleGerarNumeroInterno = async () => {
        setGerandoNumero(true);
        setError(''); // Limpa erros anteriores
        try {
            const response = await api.get('/processos/gerar-numero-interno');
            if (response.data && response.data.numero_processo) {
                setFormData({ ...formData, numero_processo: response.data.numero_processo });
            } else {
                throw new Error('Resposta inválida do servidor');
            }
        } catch (error) {
            console.error('Erro ao gerar número interno:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Erro ao gerar número interno. Tente novamente.';
            setError(errorMessage);
        } finally {
            setGerandoNumero(false);
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    // Estilos comuns para Autocomplete no modo escuro
    const autocompleteSx = {
        '& .MuiAutocomplete-paper': {
            backgroundColor: theme.palette.mode === 'dark' 
                ? theme.palette.background.paper 
                : undefined,
            color: theme.palette.text.primary
        },
        '& .MuiAutocomplete-option': {
            color: theme.palette.text.primary,
            '&.Mui-focused': {
                backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.08)' 
                    : 'rgba(0, 0, 0, 0.04)'
            },
            '&[aria-selected="true"]': {
                backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.12)' 
                    : undefined
            }
        },
        '& .MuiAutocomplete-listbox': {
                            backgroundColor: theme.palette.mode === 'dark' 
                ? theme.palette.background.paper 
                : undefined,
            color: theme.palette.text.primary
        },
        '& .MuiAutocomplete-groupLabel': {
            color: theme.palette.text.secondary,
            backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.08)' 
                : undefined
        }
    };

    return (
        <Container maxWidth="lg">
            {/* Header destacado para indicar a tela ativa */}
            <Box 
                sx={{ 
                    mb: 3, 
                    p: 2, 
                    backgroundColor: 'primary.main', 
                    color: 'primary.contrastText',
                    borderRadius: 2,
                    boxShadow: 2,
                    mt: 2
                }}
            >
                <Typography 
                    variant="h4" 
                    component="h1" 
                    sx={{ 
                        fontSize: { xs: '1.5rem', sm: '2rem' },
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <GavelIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                    {id ? 'Editar Processo' : 'Novo Processo'}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                    {id ? 'Atualize as informações do processo' : 'Cadastre um novo processo jurídico'}
                </Typography>
            </Box>

            <Paper sx={{ p: 4, mt: 2 }}>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Box>
                                <TextField
                                    required
                                    fullWidth
                                    label="Nº Processo"
                                    name="numero_processo"
                                    value={formData.numero_processo}
                                    onChange={handleChange}
                                    InputProps={{
                                        endAdornment: !id && (
                                            <InputAdornment position="end">
                                                <Button
                                                    size="small"
                                                    onClick={handleGerarNumeroInterno}
                                                    disabled={gerandoNumero}
                                                    variant="contained"
                                                    color="primary"
                                                    sx={{ minWidth: 'auto', px: 2 }}
                                                >
                                                    {gerandoNumero ? 'Gerando...' : 'Gerar'}
                                                </Button>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    Use o botão "Gerar" para criar um código de processo interno no formato ####-###### (ex: 5000-000001). 
                                    Este código é usado para processos sem número oficial, como acompanhamentos de benefícios (ex: LOAS).
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={clientes}
                                getOptionLabel={(option) => `${option.nome} - ${option.cpf || ''}`}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                value={clientes.find(c => c.id === parseInt(formData.cliente_id)) || null}
                                onChange={(e, newValue) => {
                                    setFormData({ ...formData, cliente_id: newValue ? newValue.id : '' });
                                }}
                                filterOptions={(options, { inputValue }) => {
                                    return options.filter(option => {
                                        const searchValue = inputValue.toLowerCase();
                                        return (
                                            option.nome?.toLowerCase().includes(searchValue) ||
                                            option.cpf?.toLowerCase().includes(searchValue) ||
                                            option.email?.toLowerCase().includes(searchValue)
                                        );
                                    });
                                }}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params} 
                                        required 
                                        label="Cliente" 
                                        placeholder="Busque por nome, CPF ou email..."
                                    />
                                )}
                                sx={autocompleteSx}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Data de Distribuição"
                                name="data_distribuicao"
                                type="date"
                                value={formData.data_distribuicao}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Data de Ajuizamento"
                                name="data_ajuizamento"
                                type="date"
                                value={formData.data_ajuizamento}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={statusList}
                                getOptionLabel={(option) => {
                                    if (typeof option === 'string') return option;
                                    return option.valor || '';
                                }}
                                value={getValueForAutocomplete(statusList, formData.status)}
                                onChange={(e, newValue) => {
                                    setFormData({ 
                                        ...formData, 
                                        status: newValue ? (typeof newValue === 'string' ? newValue : newValue.valor) : '' 
                                    });
                                }}
                                freeSolo
                                renderInput={(params) => (
                                    <TextField {...params} label="Status" />
                                )}
                                sx={autocompleteSx}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={tipoBeneficioList}
                                getOptionLabel={(option) => {
                                    if (typeof option === 'string') return option;
                                    return option.valor || '';
                                }}
                                value={getValueForAutocomplete(tipoBeneficioList, formData.tipo_beneficio)}
                                onChange={(e, newValue) => {
                                    setFormData({ 
                                        ...formData, 
                                        tipo_beneficio: newValue ? (typeof newValue === 'string' ? newValue : newValue.valor) : '' 
                                    });
                                }}
                                freeSolo
                                groupBy={(option) => {
                                    if (typeof option === 'string' || !option.categoria) return 'Outros';
                                    return option.categoria;
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Tipo de Benefício" />
                                )}
                                sx={autocompleteSx}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={tipoAcaoList}
                                getOptionLabel={(option) => {
                                    if (typeof option === 'string') return option;
                                    return option.valor || '';
                                }}
                                value={getValueForAutocomplete(tipoAcaoList, formData.tipo_acao)}
                                onChange={(e, newValue) => {
                                    setFormData({ 
                                        ...formData, 
                                        tipo_acao: newValue ? (typeof newValue === 'string' ? newValue : newValue.valor) : '' 
                                    });
                                }}
                                freeSolo
                                renderInput={(params) => (
                                    <TextField {...params} label="Tipo de Ação" />
                                )}
                                sx={autocompleteSx}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={varaList}
                                getOptionLabel={(option) => {
                                    if (typeof option === 'string') return option;
                                    return option.valor || '';
                                }}
                                value={getValueForAutocomplete(varaList, formData.vara)}
                                onChange={(e, newValue) => {
                                    setFormData({ 
                                        ...formData, 
                                        vara: newValue ? (typeof newValue === 'string' ? newValue : newValue.valor) : '' 
                                    });
                                }}
                                freeSolo
                                renderInput={(params) => (
                                    <TextField {...params} label="Vara" />
                                )}
                                sx={autocompleteSx}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Comarca"
                                name="comarca"
                                value={formData.comarca}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={advogados}
                                getOptionLabel={(option) => `${option.nome}${option.oab ? ' - OAB: ' + option.oab : ''}`}
                                value={advogados.find(a => a.id === parseInt(formData.advogado_id)) || null}
                                onChange={(e, newValue) => {
                                    setFormData({ ...formData, advogado_id: newValue ? newValue.id : '' });
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Advogado" />
                                )}
                                sx={autocompleteSx}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Autocomplete
                                multiple
                                options={parteExternaList}
                                getOptionLabel={(option) => {
                                    if (typeof option === 'string') return option;
                                    return option.nome_completo || '';
                                }}
                                value={parteExternaProcesso.map(p => {
                                    const parteId = p.parte_externa_id || p.id;
                                    const parte = parteExternaList.find(pe => pe.id === parteId);
                                    // Se encontrou na lista, usa ela; senão cria objeto temporário com dados do banco
                                    return parte || { 
                                        id: parteId, 
                                        nome_completo: p.nome_completo || 'Nome não encontrado',
                                        isCustom: true 
                                    };
                                })}
                                onChange={(e, newValue) => {
                                    setParteExternaProcesso(newValue.map(v => ({
                                        parte_externa_id: v.id,
                                        nome_completo: v.nome_completo
                                    })));
                                }}
                                filterOptions={(options, { inputValue }) => {
                                    return options.filter(option => {
                                        const searchValue = inputValue.toLowerCase();
                                        return option.nome_completo?.toLowerCase().includes(searchValue) ||
                                               option.cliente_nome?.toLowerCase().includes(searchValue);
                                    });
                                }}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params} 
                                        label="Partes Externas" 
                                        placeholder="Selecione as partes externas (advogados, parceiros, etc.)"
                                    />
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            label={option.nome_completo}
                                            {...getTagProps({ index })}
                                            key={option.id}
                                            sx={{
                                                backgroundColor: theme.palette.mode === 'dark'
                                                    ? 'rgba(255, 255, 255, 0.15)'
                                                    : undefined,
                                                color: theme.palette.text.primary
                                            }}
                                        />
                                    ))
                                }
                                sx={autocompleteSx}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Pasta"
                                name="pasta"
                                value={formData.pasta}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Valor da Causa"
                                name="valor_causa"
                                type="number"
                                value={formData.valor_causa}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Matéria"
                                name="materia"
                                value={formData.materia || ''}
                                onChange={handleChange}
                                helperText="Campo livre (preenchido originalmente pela coluna MATERIA da aba Diversas)"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth>
                                <InputLabel>Situação</InputLabel>
                                <Select
                                    label="Situação"
                                    name="situacao"
                                    value={(formData.situacao || '').toUpperCase()}
                                    onChange={handleChange}
                                >
                                    <MenuItem value="">Selecione...</MenuItem>
                                    <MenuItem value="ATIVO">Ativo</MenuItem>
                                    <MenuItem value="INATIVO">Inativo</MenuItem>
                                    <MenuItem value="ARQUIVADO">Arquivado</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Tempo de Contribuição"
                                name="tempo_contribuicao"
                                value={formData.tempo_contribuicao}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={localizacaoList}
                                getOptionLabel={(option) => {
                                    if (typeof option === 'string') return option;
                                    return option.valor || '';
                                }}
                                value={getValueForAutocomplete(localizacaoList, formData.localizacao)}
                                onChange={(e, newValue) => {
                                    setFormData({ 
                                        ...formData, 
                                        localizacao: newValue ? (typeof newValue === 'string' ? newValue : newValue.valor) : '' 
                                    });
                                }}
                                freeSolo
                                renderInput={(params) => (
                                    <TextField {...params} label="Localização" />
                                )}
                                sx={autocompleteSx}
                            />
                        </Grid>
                        {id && (
                            <>
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="h6" gutterBottom>
                                        Histórico Geral (Somente Consulta)
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        label="Histórico Geral"
                                        name="historico_geral"
                                        value={formData.historico_geral || ''}
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                        sx={{
                                            '& .MuiInputBase-input': {
                                                backgroundColor: theme.palette.mode === 'dark' 
                                                    ? 'rgba(255, 255, 255, 0.05)' 
                                                    : theme.palette.grey[50],
                                                cursor: 'not-allowed',
                                                color: theme.palette.text.primary
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="h6">
                                            Andamentos ({andamentos.length})
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            startIcon={<HistoryIcon />}
                                            onClick={() => navigate(`/processos/${id}/andamentos`)}
                                        >
                                            Gerenciar Andamentos
                                        </Button>
                                    </Box>
                                    {andamentos.length > 0 ? (
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ 
                                                            fontWeight: 600,
                                                            backgroundColor: theme.palette.mode === 'dark' 
                                                                ? 'rgba(255, 255, 255, 0.08)' 
                                                                : undefined,
                                                            color: theme.palette.text.primary
                                                        }}>
                                                            Data
                                                        </TableCell>
                                                        <TableCell sx={{ 
                                                            fontWeight: 600,
                                                            backgroundColor: theme.palette.mode === 'dark' 
                                                                ? 'rgba(255, 255, 255, 0.08)' 
                                                                : undefined,
                                                            color: theme.palette.text.primary
                                                        }}>
                                                            Tipo
                                                        </TableCell>
                                                        <TableCell sx={{ 
                                                            fontWeight: 600,
                                                            backgroundColor: theme.palette.mode === 'dark' 
                                                                ? 'rgba(255, 255, 255, 0.08)' 
                                                                : undefined,
                                                            color: theme.palette.text.primary
                                                        }}>
                                                            Descrição
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {andamentos.slice(0, 5).map((andamento) => (
                                                        <TableRow 
                                                            key={andamento.id}
                                                            sx={{
                                                                '&:hover': {
                                                                    backgroundColor: theme.palette.mode === 'dark' 
                                                                        ? 'rgba(255, 255, 255, 0.05)' 
                                                                        : 'rgba(0, 0, 0, 0.04)'
                                                                }
                                                            }}
                                                        >
                                                            <TableCell sx={{ color: theme.palette.text.primary }}>
                                                                {formatarDataExibicao(andamento.data_andamento)}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip 
                                                                    label={andamento.tipo_andamento || 'OUTROS'} 
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor: theme.palette.mode === 'dark'
                                                                            ? 'rgba(255, 255, 255, 0.15)'
                                                                            : undefined,
                                                                        color: theme.palette.text.primary,
                                                                        fontWeight: 500
                                                                    }}
                                                                />
                                                            </TableCell>
                                                            <TableCell sx={{ color: theme.palette.text.primary }}>
                                                                {andamento.descricao || '-'}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                            {andamentos.length > 5 && (
                                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Mostrando 5 de {andamentos.length} andamentos. 
                                                        Clique em "Gerenciar Andamentos" para ver todos.
                                                    </Typography>
                                                </Box>
                                            )}
                                        </TableContainer>
                                    ) : (
                                        <Paper sx={{ 
                                            p: 2, 
                                            textAlign: 'center', 
                                            bgcolor: theme.palette.mode === 'dark' 
                                                ? 'rgba(255, 255, 255, 0.05)' 
                                                : theme.palette.grey[50],
                                            color: theme.palette.text.primary
                                        }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Nenhum andamento cadastrado. Clique em "Gerenciar Andamentos" para adicionar.
                                            </Typography>
                                        </Paper>
                                    )}
                                </Grid>
                            </>
                        )}
                    </Grid>

                    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                        >
                            Salvar
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/processos')}
                        >
                            Cancelar
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Container>
    );
}

export default ProcessoForm;

